import { useState } from "react";
import { C } from "../../constants";
import { Ic } from "../../components/ui/Icons";
import { apiCall } from "../../api";
import FileUpload from "../../components/ui/FileUpload";

// ─── Field display with edit capability ───
function DetailField({ label, labelEs, value, onSave, type = "text", options }) {
  const [editing, setEditing] = useState(false);
  const [editVal, setEditVal] = useState(value || "");

  const save = () => { onSave(editVal); setEditing(false); };
  const cancel = () => { setEditVal(value || ""); setEditing(false); };

  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "flex-start", gap: 12 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>
          {label} {labelEs && <span style={{ fontStyle: "italic", textTransform: "none", letterSpacing: 0 }}>/ {labelEs}</span>}
        </div>
        {editing ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            {options ? (
              <select value={editVal} onChange={e => setEditVal(e.target.value)}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit" }}>
                <option value="">--</option>
                {options.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : type === "textarea" ? (
              <textarea value={editVal} onChange={e => setEditVal(e.target.value)} rows={3}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit", resize: "vertical" }} />
            ) : (
              <input type={type} value={editVal} onChange={e => setEditVal(e.target.value)} autoFocus
                onKeyDown={e => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
                style={{ flex: 1, padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.navy}40`, background: C.grayBg, fontSize: 13, color: C.t1, outline: "none", fontFamily: "inherit" }} />
            )}
            <button onClick={save} style={{ background: C.navy, border: "none", borderRadius: 6, padding: "6px 8px", cursor: "pointer", color: C.white, display: "flex" }}><Ic.Check /></button>
            <button onClick={cancel} style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px", cursor: "pointer", color: C.t3, display: "flex" }}><Ic.X /></button>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: value ? C.t1 : C.t3, fontWeight: 400 }}>{value || "—"}</div>
        )}
      </div>
      {!editing && onSave && (
        <button onClick={() => { setEditVal(value || ""); setEditing(true); }}
          style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, display: "flex", padding: 4, borderRadius: 6, transition: "all 0.2s", flexShrink: 0, marginTop: 2 }}
          onMouseEnter={e => { e.currentTarget.style.color = C.navy; e.currentTarget.style.background = C.blueLight; }}
          onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.background = "none"; }}>
          <Ic.Edit />
        </button>
      )}
    </div>
  );
}

function DetailSection({ title }) {
  return (
    <div style={{ padding: "16px 0 8px", borderBottom: `2px solid ${C.navy}15` }}>
      <h4 style={{ fontSize: 14, fontWeight: 700, color: C.navy, margin: 0 }}>{title}</h4>
    </div>
  );
}

// ─── Status badge (read-only display) ───
function StatusBadge({ status }) {
  const m = {
    pending:  { bg: "#FEF3C7", c: "#92400E", label: "Pending" },
    review:   { bg: "#E0E7FF", c: "#3730A3", label: "In Review" },
    quoted:   { bg: "#D1FAE5", c: "#065F46", label: "Quoted" },
    rejected: { bg: "#FEE2E2", c: "#991B1B", label: "Rejected" },
  };
  const s = m[status] || m.pending;
  return (
    <span style={{ display: "inline-block", padding: "3px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: s.bg, color: s.c }}>
      {s.label}
    </span>
  );
}

// ─── Admin-only status toggle ───
function StatusToggle({ currentStatus, requestId, onStatusChange }) {
  const [saving, setSaving] = useState(false);
  const statuses = ["pending", "review", "quoted", "rejected"];
  const labels = { pending: "Pending", review: "In Review", quoted: "Quoted", rejected: "Rejected" };
  const colors = {
    pending:  { bg: "#FEF3C7", c: "#92400E", border: "#FCD34D" },
    review:   { bg: "#E0E7FF", c: "#3730A3", border: "#A5B4FC" },
    quoted:   { bg: "#D1FAE5", c: "#065F46", border: "#6EE7B7" },
    rejected: { bg: "#FEE2E2", c: "#991B1B", border: "#FCA5A5" },
  };

  const handleChange = async (newStatus) => {
    if (newStatus === currentStatus) return;
    setSaving(true);
    try {
      await apiCall("updateRequestStatus", { requestId, status: newStatus });
      onStatusChange(newStatus);
    } catch (err) {
      console.error("Failed to update status:", err);
    }
    setSaving(false);
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <span style={{ fontSize: 11, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
        Status{saving ? " · Saving..." : ""}
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        {statuses.map(s => {
          const active = s === currentStatus;
          const col = colors[s];
          return (
            <button key={s} onClick={() => handleChange(s)} disabled={saving}
              style={{
                padding: "4px 12px", borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                border: `1.5px solid ${active ? col.border : C.border}`,
                background: active ? col.bg : C.white,
                color: active ? col.c : C.t3,
                transition: "all 0.15s", fontFamily: "inherit",
                opacity: saving ? 0.6 : 1,
              }}
              onMouseEnter={e => { if (!active && !saving) { e.currentTarget.style.background = col.bg; e.currentTarget.style.color = col.c; e.currentTarget.style.borderColor = col.border; }}}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = C.white; e.currentTarget.style.color = C.t3; e.currentTarget.style.borderColor = C.border; }}}
            >
              {labels[s]}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Product Request detail view ───
function ProductRequestDetail({ submission }) {
  let items = [];
  try {
    const raw = submission.items;
    if (raw && raw !== "" && raw !== "[]") {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) items = parsed;
    }
  } catch { items = []; }

  let fd = {};
  try { fd = JSON.parse(submission.formData || "{}"); } catch { fd = {}; }

  const name = submission.contactName || fd.name || "";
  const email = submission.email || fd.email || "";
  const company = submission.company || fd.company || "";
  const manufacturer = fd.manufacturer || submission.type?.replace("Product Request: ", "") || "";
  const totalQty = items.reduce((s, i) => s + (parseInt(i.qty) || 0), 0);

  return (
    <div style={{ padding: "8px 28px 28px" }}>
      <DetailSection title="Contact Information" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0 }}>
        <DetailField label="Name" value={name} />
        <DetailField label="Email" value={email} />
        <DetailField label="Company" value={company} />
        <DetailField label="Manufacturer" value={manufacturer} />
      </div>

      <DetailSection title={`Requested Products — ${items.length} line${items.length !== 1 ? "s" : ""}`} />
      {items.length === 0 ? (
        <p style={{ fontSize: 13, color: C.t3, padding: "12px 0" }}>
          No product details found. Please refresh — if this persists, contact support.
        </p>
      ) : (
        <div style={{ overflowX: "auto", marginTop: 12, borderRadius: 10, border: `1px solid ${C.border}` }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: C.grayBg }}>
                {["Category", "Part Number", "Description", "Delivery Location", "Qty"].map((col, i) => (
                  <th key={col} style={{
                    padding: "10px 12px", textAlign: i === 4 ? "center" : "left",
                    fontWeight: 600, color: C.t2, fontSize: 12,
                    borderBottom: `2px solid ${C.border}`, whiteSpace: "nowrap",
                  }}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} style={{
                  background: idx % 2 === 0 ? C.white : C.grayBg,
                  borderBottom: idx < items.length - 1 ? `1px solid ${C.border}` : "none",
                }}>
                  <td style={{ padding: "10px 12px", color: C.t2, fontSize: 12 }}>{item.category}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600, color: C.t1, whiteSpace: "nowrap" }}>{item.partNumber}</td>
                  <td style={{ padding: "10px 12px", color: C.t1, lineHeight: 1.4 }}>{item.description}</td>
                  <td style={{ padding: "10px 12px", color: C.t2, fontSize: 12, whiteSpace: "nowrap" }}>{item.incoterms}</td>
                  <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: C.t1 }}>{item.qty}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: C.grayBg, borderTop: `2px solid ${C.border}` }}>
                <td colSpan={4} style={{ padding: "10px 12px", fontWeight: 700, color: C.t2, fontSize: 12 }}>Total Quantity</td>
                <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700, color: C.navy, fontSize: 14 }}>{totalQty}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

const FIELD_LABELS = {
  projectName: ["Project Name", "Nombre del Proyecto"],
  projectLocation: ["Project Location", "Ubicación del Proyecto"],
  endClient: ["End Client", "Cliente Final"],
  estimatedClosingDate: ["Estimated Closing Date", "Fecha de Cierre"],
  estimatedExecDate: ["Estimated Execution Date", "Fecha de Ejecución"],
  systemType: ["System Type", "Tipo de Sistema"],
  designConcentration: ["Design Concentration", "Concentración de Diseño"],
  riskArea: ["Risk Area", "Área de Riesgo"],
  protectionTarget: ["What is Being Protected", "Qué se Protege"],
  riskType: ["Risk Type", "Tipo de Riesgo"],
  specialMaterials: ["Special Materials", "Materiales Especiales"],
  normallyOccupied: ["Normally Occupied", "Normalmente Ocupada"],
  unitSystem: ["Unit System", "Sistema de Unidades"],
  minTemp: ["Min Temperature", "Temp. Mínima"],
  maxTemp: ["Max Temperature", "Temp. Máxima"],
  elevation: ["Elevation", "Elevación"],
  isSealed: ["Enclosure Sealed", "Recinto Hermético"],
  openings: ["Openings", "Aperturas"],
  hvacShutdown: ["HVAC Shutdown on Discharge", "HVAC al Disparo"],
  mainLength: ["Main Length", "Largo Principal"],
  mainWidth: ["Main Width", "Ancho Principal"],
  mainHeight: ["Main Height", "Alto Principal"],
  falseFloorLength: ["Raised Floor Length", "Largo Piso Falso"],
  falseFloorWidth: ["Raised Floor Width", "Ancho Piso Falso"],
  falseFloorHeight: ["Raised Floor Height", "Alto Piso Falso"],
  falseCeilingLength: ["False Ceiling Length", "Largo Techo Falso"],
  falseCeilingWidth: ["False Ceiling Width", "Ancho Techo Falso"],
  falseCeilingHeight: ["False Ceiling Height", "Alto Techo Falso"],
  totalVolume: ["Total Volume", "Volumen Total"],
  additionalVolume: ["Additional Volume", "Volumen Adicional"],
  protectionAreas: ["Areas to Protect", "Áreas a Proteger"],
  cylinderLocation: ["Cylinder Location", "Ubicación del Cilindro"],
  cylinderColor: ["Cylinder Color", "Color del Cilindro"],
  pressureSupervision: ["Pressure Supervision", "Supervisión de Presión"],
  liquidLevel: ["Liquid Level Indicator", "Indicador de Nivel"],
  dischargePressure: ["Discharge Pressure", "Presión de Descarga"],
  solenoidType: ["Solenoid Type", "Tipo de Solenoide"],
  warningSigns: ["Warning Signs", "Carteles de Advertencia"],
  microPressure: ["Micro Pressure Supervision", "Supervisión Micro"],
  microDetectionType: ["Detection & Discharge Type", "Tipo Detección/Descarga"],
  microSolenoid: ["Micro Solenoid", "Solenoide Micro"],
  needsPanel: ["Include Fire Panel", "Panel de Incendio"],
  panelType: ["Panel Type", "Tipo de Panel"],
  detectionType: ["Detection Type", "Tipo de Detección"],
  pullStations: ["Pull Stations", "Estaciones Manuales"],
  abortSwitch: ["Abort Switch", "Switch de Aborto"],
  maintenanceSwitch: ["Maintenance Switch", "Switch Mantenimiento"],
  strobeHorns: ["Strobe/Horns", "Estrobos/Sirenas"],
  fireBell: ["Fire Bell", "Campana"],
  monitoringModules: ["Monitor Modules", "Módulos de Monitoreo"],
  relayModules: ["Relay Modules", "Módulos de Relay"],
  additionalNotes: ["Additional Notes", "Notas Adicionales"],
  concentration: ["Design Concentration", "Concentración"],
  cylinderConfig: ["Cylinder Configuration", "Config. Cilindro"],
  pressureSupervisor: ["Pressure Supervisor", "Supervisor Presión"],
  supervisableSolenoid: ["Supervisable Solenoid", "Solenoide"],
  dischargeSupervisor: ["Discharge Supervisor", "Supervisor Descarga"],
  nozzleType: ["Nozzle Type", "Tipo Boquilla"],
  lli: ["Liquid Level Indicator", "Indicador Nivel"],
  isAirtight: ["Airtight Enclosure", "Recinto Hermético"],
  mainL: ["Enclosure 1 - Length", "Recinto 1 - Largo"],
  mainW: ["Enclosure 1 - Width", "Recinto 1 - Ancho"],
  mainH: ["Enclosure 1 - Height", "Recinto 1 - Alto"],
  floorL: ["Enclosure 2 - Length", "Recinto 2 - Largo"],
  floorW: ["Enclosure 2 - Width", "Recinto 2 - Ancho"],
  floorH: ["Enclosure 2 - Height", "Recinto 2 - Alto"],
  ceilingL: ["Enclosure 3 - Length", "Recinto 3 - Largo"],
  ceilingW: ["Enclosure 3 - Width", "Recinto 3 - Ancho"],
  ceilingH: ["Enclosure 3 - Height", "Recinto 3 - Alto"],
  designerName: ["Designer", "Diseñador"],
  phone: ["Phone", "Teléfono"],
  notes: ["Notes", "Notas"],
  country: ["Country", "País"],
  detectionObjective: ["Detection Objective", "Objetivo de Detección"],
  sensitivity: ["Sensitivity", "Sensibilidad"],
  designStandard: ["Design Standard", "Normativa de Diseño"],
  estimatedTemp: ["Estimated Temperature", "Temperatura Estimada"],
  environment: ["Environment Type", "Tipo de Ambiente"],
  hasHVAC: ["Has HVAC", "Tiene HVAC"],
  hvacAirSupplyLocation: ["Air Supply Location", "Ubicación Suministro de Aire"],
  ceilingType: ["Ceiling Type", "Tipo de Techo"],
  needsSuppression: ["Needs Suppression", "Requiere Supresión"],
  gasValve: ["Gas Shut-off Valve", "Válvula de Gas"],
  alarmStrobes: ["Alarm/Strobes", "Alarma/Estrobos"],
  includePanel: ["Include Panel", "Incluir Panel"],
  hoodLength: ["Hood Length", "Largo Campana"],
  hoodWidth: ["Hood Width", "Ancho Campana"],
  equipmentLength: ["Equipment Length", "Largo Equipos"],
  equipmentWidth: ["Equipment Width", "Ancho Equipos"],
  ductLength: ["Duct Length", "Largo Ducto"],
  ductWidth: ["Duct Width", "Ancho Ducto"],
  hoodToEquipmentDistance: ["Hood to Equipment Distance", "Distancia Campana-Equipos"],
  powerCutOnDischarge: ["Power Cut on Discharge", "Corte de Corriente"],
  activationType: ["Activation Type", "Tipo de Activación"],
  suppressionRelease: ["Suppression Release", "Estación de Descarga"],
  smokeDetectors: ["Smoke Detectors", "Detectores de Humo"],
  heatDetectors: ["Heat Detectors", "Detectores de Calor"],
  aspiratingDetection: ["Aspirating Detection", "Detección por Aspiración"],
  ap1Length: ["Aperture 1 Length", "Apertura 1 Largo"],
  ap1Width: ["Aperture 1 Width", "Apertura 1 Ancho"],
  ap1Height: ["Aperture 1 Height", "Apertura 1 Alto"],
  ap2Length: ["Aperture 2 Length", "Apertura 2 Largo"],
  ap2Width: ["Aperture 2 Width", "Apertura 2 Ancho"],
  ap2Height: ["Aperture 2 Height", "Apertura 2 Alto"],
  env1Length: ["Env. 1 Length", "Amb. 1 Largo"],
  env1Width: ["Env. 1 Width", "Amb. 1 Ancho"],
  env1Height: ["Env. 1 Height", "Amb. 1 Alto"],
  env2Length: ["Env. 2 Length", "Amb. 2 Largo"],
  env2Width: ["Env. 2 Width", "Amb. 2 Ancho"],
  env2Height: ["Env. 2 Height", "Amb. 2 Alto"],
  env3Length: ["Env. 3 Length", "Amb. 3 Largo"],
  env3Width: ["Env. 3 Width", "Amb. 3 Ancho"],
  env3Height: ["Env. 3 Height", "Amb. 3 Alto"],
};

const SKIP_KEYS = ["solutionType", "submittedBy", "bomResult"];

export default function SubmissionDetail({ submission, onClose, onUpdate, isAdmin }) {
  const [formData, setFormData] = useState(() => {
    try { return JSON.parse(submission.formData || "{}"); } catch { return {}; }
  });
  const [currentStatus, setCurrentStatus] = useState(submission.status || "pending");
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState(() => {
    if (Array.isArray(submission.documents)) return submission.documents;
    if (typeof submission.documents === "string" && submission.documents) {
      try { return JSON.parse(submission.documents); } catch { return []; }
    }
    return [];
  });
  const [newFiles, setNewFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const isProductRequest = (submission.type || "").startsWith("Product Request:");

  const updateField = async (key, value) => {
    const updated = { ...formData, [key]: value };
    setFormData(updated);
    setSaving(true);
    try {
      await apiCall("updateRequest", { requestId: submission.id, formData: updated });
      if (onUpdate) onUpdate();
    } catch (err) { console.error("Failed to save:", err); }
    setSaving(false);
  };

  const uploadNewFiles = async () => {
    if (newFiles.length === 0) return;
    setUploading(true);
    try {
      const r = await apiCall("uploadFiles", {
        requestId: submission.id,
        files: newFiles.map(f => ({ name: f.name, mimeType: f.mimeType, base64: f.base64 })),
      });
      if (r.success) { setDocs(r.documents || []); setNewFiles([]); if (onUpdate) onUpdate(); }
      else alert("Upload failed: " + (r.error || "Unknown error"));
    } catch (err) {
      console.error("Failed to upload:", err);
      alert("Upload failed. The file may be too large or there was a connection error.");
    }
    setUploading(false);
  };

  const deleteFile = async (fileId) => {
    if (!window.confirm("Delete this file? / ¿Eliminar este archivo?")) return;
    try {
      const r = await apiCall("deleteFile", { requestId: submission.id, fileId });
      if (r.success) { setDocs(r.documents); if (onUpdate) onUpdate(); }
    } catch (err) { console.error("Failed to delete:", err); }
  };

  const formKeys = Object.keys(formData).filter(k => !SKIP_KEYS.includes(k) && formData[k] !== undefined);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "flex-end", zIndex: 1000 }}
      onClick={onClose}>
      <div style={{ width: "100%", maxWidth: 620, height: "100%", background: C.white, boxShadow: "-8px 0 40px rgba(0,0,0,0.12)", overflowY: "auto", animation: "slideIn 0.25s ease" }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 28px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: C.white, zIndex: 1 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 18, fontWeight: 700, color: C.info }}>{submission.id}</span>
              <StatusBadge status={currentStatus} />
              {saving && <span style={{ fontSize: 11, color: C.t3 }}>Saving...</span>}
            </div>
            <div style={{ fontSize: 13, color: C.t2 }}>{submission.type}</div>
          </div>
          <button onClick={onClose}
            style={{ background: C.grayBg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 10px", cursor: "pointer", color: C.t3, display: "flex" }}
            onMouseEnter={e => { e.currentTarget.style.color = C.t1; e.currentTarget.style.borderColor = C.t3; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.borderColor = C.border; }}>
            <Ic.X />
          </button>
        </div>

        {/* Admin status toggle bar */}
        {isAdmin && (
          <div style={{ padding: "12px 28px", background: `${C.navy}06`, borderBottom: `1px solid ${C.border}` }}>
            <StatusToggle
              currentStatus={currentStatus}
              requestId={submission.id}
              onStatusChange={newStatus => {
                setCurrentStatus(newStatus);
                if (onUpdate) onUpdate();
              }}
            />
          </div>
        )}

        {/* Meta */}
        <div style={{ padding: "16px 28px", background: C.grayBg, borderBottom: `1px solid ${C.border}`, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
          <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Submitted By</div><div style={{ fontSize: 13, color: C.t1, fontWeight: 500 }}>{submission.contactName}</div></div>
          <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Date Submitted</div><div style={{ fontSize: 13, color: C.t1 }}>{submission.date ? submission.date.toString().slice(0, 10) : ""}</div></div>
          <div><div style={{ fontSize: 10, color: C.t3, textTransform: "uppercase", letterSpacing: "0.05em" }}>Last Updated</div><div style={{ fontSize: 13, color: C.t1 }}>{submission.lastUpdated ? submission.lastUpdated.toString().slice(0, 10) : ""}</div></div>
        </div>

        {/* Route to correct detail view */}
        {isProductRequest ? (
          <ProductRequestDetail submission={submission} />
        ) : (
          <div style={{ padding: "8px 28px 20px" }}>
            <DetailSection title="Request Details / Detalles de la Solicitud" />
            {formKeys.map(key => {
              const labels = FIELD_LABELS[key];
              return (
                <DetailField key={key}
                  label={labels ? labels[0] : key}
                  labelEs={labels ? labels[1] : ""}
                  value={formData[key]}
                  onSave={v => updateField(key, v)}
                />
              );
            })}

            <DetailSection title="Documents / Documentos" />
            {docs.length > 0 ? (
              <div style={{ marginTop: 12 }}>
                {docs.map((doc, i) => (
                  <div key={doc.id || i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 8, background: C.grayBg, border: `1px solid ${C.border}`, marginBottom: 6 }}>
                    <span style={{ fontSize: 16 }}>📄</span>
                    <span onClick={(e) => { e.stopPropagation(); window.open(doc.url, "_blank"); }}
                      style={{ flex: 1, fontSize: 13, color: C.info, cursor: "pointer", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      onMouseEnter={e => e.target.style.textDecoration = "underline"}
                      onMouseLeave={e => e.target.style.textDecoration = "none"}>
                      {doc.name}
                    </span>
                    <button onClick={() => deleteFile(doc.id)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: C.t3, fontSize: 16, padding: "2px 6px", borderRadius: 4, transition: "all 0.15s" }}
                      onMouseEnter={e => { e.currentTarget.style.color = C.red; e.currentTarget.style.background = "#FEF2F2"; }}
                      onMouseLeave={e => { e.currentTarget.style.color = C.t3; e.currentTarget.style.background = "none"; }}
                      title="Delete file">×</button>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: 13, color: C.t3, padding: "12px 0" }}>No documents attached.</p>
            )}

            <div style={{ marginTop: 16 }}>
              <FileUpload files={newFiles} onChange={setNewFiles} />
              {newFiles.length > 0 && (
                <div style={{ marginTop: 12, display: "flex", justifyContent: "flex-end" }}>
                  <button onClick={uploadNewFiles} disabled={uploading}
                    style={{ padding: "8px 20px", borderRadius: 8, border: "none", background: C.navy, color: C.white, fontSize: 13, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: uploading ? 0.7 : 1 }}>
                    {uploading ? "Uploading..." : `Upload ${newFiles.length} file${newFiles.length > 1 ? "s" : ""}`}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>
    </div>
  );
}
